<template>
  <div class="relative" v-bind:style="mainPanelMargin">
    <Banner :alert="alert" @close="onBannerClose" />
    <GoogleText ref="googleSearch"></GoogleText>
    <GoogleMapSpinner
      ref="googleMapSpinner"
      :mapSize="mapSize"
      @loading="loadingUpdate"
    ></GoogleMapSpinner>
    <div ref="googleMap" class="overflow-hidden" :style="mapSize"></div>
    <MapSideBar
      :mapElementIds="mapElementIds"
      :components="components"
      :expandedWidth="sideBarWidth"
      :availableHeight="availableHeight"
      :showFilterPanel="showFilterPanel"
      :loading="loading"
      :poiTypes="poiTypes"
      @event="handleSideBarEvent"
      @expanded="adjustWidth"
    ></MapSideBar>
  </div>
</template>

<script>
/* eslint-disable */
import LocationMapMixin from '@/mixins/LocationMapMixin'
import LocationMapPOIMixin from '@/mixins/LocationMapPOIMixin'
import MarkerClusterer from '@google/markerclustererplus'
import Banner from '../components/Banner'
import MapSideBar from '../components/mappings/MapSideBar'
import GoogleText from '../components/mappings/GoogleText'
import GoogleMapSpinner from '../components/mappings/GoogleMapSpinner'
import LocationMapService from '../services/LocationMapService'

export default {
  /**
   * Name of the component
   */
  name: 'MultiLocationMap',

  /**
   * This components mixins
   */
  mixins: [LocationMapMixin, LocationMapPOIMixin],

  /**
   * Components
   */
  components: {
    Banner,
    MapSideBar,
    GoogleText,
    GoogleMapSpinner,
  },

  /**
   * This components properties
   */
  props: ['dashboardMapId', 'showFilterPanel'],

  /**
   * This component's data attributes
   */
  data() {
    return {
      mapElementIds: [],
      components: [],
      poiTypes: [],
      isSideBarExpanded: this.showFilterPanel === 'true',
      sideBarWidth: '5px',
      markerCluster: null,
      markersMap: new Map(),
      componentFilterMap: new Map(),
      maxZoomService: null,
      loading: false,
    }
  },

  /**
   * Computed properties
   */
  computed: {
    mainPanelMargin() {
      return {
        marginLeft: this.isSideBarExpanded ? this.getSideBarWidth() : '0px',
      }
    },
    mapSize() {
      return {
        width: this.isSideBarExpanded
          ? `${
              this.availableWidth > 1000
                ? this.availableWidth * (2 / 3)
                : this.availableWidth * (1 / 2)
            }px`
          : `${parseInt(this.fullSize.width, 10)}px`,
        height: `${parseInt(this.fullSize.height, 10)}px`,
      }
    },
  },

  /**
   * Initialize
   */
  async mounted() {
    await this.initializeComponent()
    if (this.dashboardMapId) {
      const self = this
      try {
        // initialize the marker cluster
        const options = { imagePath: 'images/m', maxZoom: 22 }
        self.markerCluster = new MarkerClusterer(self.map, [], options)
        self.maxZoomService = new self.google.maps.MaxZoomService()
        self.google.maps.event.addListener(self.markerCluster, 'clusterclick', async (cluster) => {
          self.maxZoomService.getMaxZoomAtLatLng(
            { lat: cluster.getCenter().lat(), lng: cluster.getCenter().lng() },
            (result) => {
              if (result.status === 'OK') {
                if (self.map.getZoom() >= result.zoom) {
                  self._showClusterInfo(cluster)
                }
              }
            },
          )
        })

        // initialize the filter panel data
        await LocationMapService.getMapConfig(this.dashboardMapId).then((config) => {
          if (config.data) {
            self.mapElementIds = config.data.elements
            self.components = config.data.components
            self.poiTypes = config.data.poiTypes
            if (self.poiTypes.length > 0) {
              self.initPointsOfInterest()
              this.map.addListener('idle', () => this.loadActivatedGlobalPointsOfInterest())
            }
          }
        })
      } catch (error) {
        const message = error.response ? error.response.data : error
        this.alert = { type: 'error', message }
      }
    }
  },

  /**
   * Component methods
   */
  methods: {
    /**
     * Retrives the location data for the specified dashboardId
     * @param mapElementId mapElementId to retrieve
     */
    async getLocations(mapElementId) {
      const self = this
      // indicate that loading started
      this.$refs.googleMapSpinner.loadingStart()
      // get relevant component filters
      const config = this.mapElementIds.filter((element) => element.id === mapElementId)[0]
      const icon =
        config.icon === ''
          ? undefined
          : { url: config.icon, scaledSize: new self.google.maps.Size(44, 44) }
      const params = this._getComponentFiltersParam(config)
      try {
        await LocationMapService.get(mapElementId, params).then((locations) => {
          // indicate that loading finished
          this.$refs.googleMapSpinner.loadingStop()
          const markers = locations.data.map((location) => {
            const marker = new self.google.maps.Marker({
              position: location.location,
              icon,
              objId: location.objId,
              objType: location.objType,
            })
            self.google.maps.event.addListener(marker, 'click', () => {
              LocationMapService.getSummary(marker.objId, marker.objType).then((summary) =>
                self.openInfoWindow(summary.data, marker),
              )
            })
            return marker
          })
          if (self.markerCluster) {
            self.markerCluster.addMarkers(markers)
            self.markersMap.set(mapElementId, markers)
            self._showCluster()
          }
        })
      } catch (error) {
        // no op
        // indicate that loading finished
        this.$refs.googleMapSpinner.loadingStop()
      }
    },
    addFeatureToMap(latLng, eventPrefix) {
      // do nothing
    },
    /**
     * display the marker cluster appropriately
     */
    _showCluster() {
      const allMarkers = this.markerCluster.getMarkers()
      const bounds = new this.google.maps.LatLngBounds()
      if (allMarkers.length > 0) {
        allMarkers.forEach((marker) => bounds.extend(marker.getPosition()))
        this.map.fitBounds(bounds)
        this.map.setCenter(bounds.getCenter())
      }
    },
    /**
     * display cluster info window
     */
    async _showClusterInfo(cluster) {
      if (cluster) {
        const summaries = await Promise.all(
          cluster.getMarkers().map((marker) => {
            return LocationMapService.getSummary(marker.objId, marker.objType).then(
              (summary) => summary.data,
            )
          }),
        )
        const content = `<div style="max-height: ${this.infoWindowHeight}; overflow: auto;">
          ${summaries.join('<br><hr style="border-top: 1px dashed"><br>')}</div>`
        this.openInfoWindow(content, null, cluster.getCenter())
      }
    },
    /**
     * get the component filters applied for a map element id
     * @param map element config
     */
    _getComponentFiltersParam(config) {
      const relCompIds = config.filters
        .filter((filter) => this.componentFilterMap.has(filter.filterComponent))
        .map((filter) => filter.filterComponent)
      const relAttrIds = config.filters
        .filter((filter) => this.componentFilterMap.has(filter.filterComponent))
        .map((filter) => filter.filterRelPath)
      const relAttrValues = relCompIds.map((compId) => this.componentFilterMap.get(compId))
      return {
        relCompIds,
        relAttrIds,
        relAttrValues,
      }
    },
    /**
     * handler for alert close
     */
    onBannerClose() {
      this.alert = null
    },
    /**
     * handler for side bar events
     * @param event Event to process
     */
    handleSideBarEvent(event) {
      // always close info window if open
      if (this.infoWindow) {
        this.infoWindow.close()
      }
      if (event.type === 'mapFilterOptionUpdated') {
        if (event.value) {
          this.getLocations(event.id)
        } else {
          this._clearMarkers(event.id)
          this._showCluster()
        }
      } else if (event.type === 'mapComponentFilterUpdated') {
        // update the component filter value map
        this.componentFilterMap.set(event.component, event.value)
        if (event.value === null) {
          this.componentFilterMap.delete(event.component)
        }
        // identify map elements effected by this component filter
        const effectedElements = this.mapElementIds.filter(
          (element) =>
            element.filters.length > 0 &&
            element.filters.filter((filter) => filter.filterComponent === event.component).length >
              0,
        )
        // filter out the currently toggled ON elements
        const activeElements = effectedElements.filter((element) => this.markersMap.has(element.id))
        // refresh each of the active elements
        activeElements.forEach((element) => {
          this._clearMarkers(element.id)
          this.getLocations(element.id)
        })
      } else if (event.type === 'poiTypeOptionUpdated') {
        if (event.value) {
          this.loadGlobalPointsOfInterest(event.id)
        } else {
          this.clearGlobalPointsOfInterest(event.id)
        }
      }
    },
    /**
     * clear markers for given element id
     * @param id element id to clear
     */
    _clearMarkers(id) {
      const markers = this.markersMap.get(id)
      this.markerCluster.removeMarkers(markers)
      this.markersMap.delete(id)
      if (this.markersMap.size < 1) {
        this.map.setZoom(2)
      }
    },
    /**
     * handler for side bar expansion/ collapse
     * @param value side bar expanded flag
     */
    adjustWidth(value) {
      this.isSideBarExpanded = value
    },
    /**
     * get the current side bar width based on the full size
     */
    getSideBarWidth() {
      this.sideBarWidth = `${
        this.availableWidth > 1000 ? this.availableWidth * (1 / 3) : this.availableWidth * (1 / 2)
      }px`
      return this.sideBarWidth
    },
    /**
     * Marks a position on the map and relays the event
     */
    _processPositionChange() {
      // position change not applicable
      return undefined
    },
    /**
     * Updates the current loading status
     */
    loadingUpdate(value) {
      this.loading = value
    },
  },
}
</script>
<style lang="postcss" scoped>
.relative {
  @apply overflow-hidden;
  transition: 0.5s;
}
</style>
